/**
 * StepEditor - Modal for creating/editing sequence steps
 * Handles all step types and branch condition configuration
 */

import {
  ASSIGNED_AGENT_HELP,
  ASSIGNED_AGENT_VAR,
  checkFallbackReplyTo,
  checkReplyToValue,
  isAssignedAgentToken,
} from "../../utils/senderVariable";
import React, { useState, useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ChevronUpIcon,
  ChevronDownIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import Modal from "../ui/Modal";
import Button from "../common/Button";
import Input from "../common/Input";
import StepTypeIcon from "./StepTypeIcon";
import { getLists } from "../../services/lists.service";
import {
  getSequenceStepsFlat,
  reorderBranchSiblings,
} from "../../services/sequences.service";
import {
  branchOrderWrites,
  moveInOrder,
  nextBranchOrder,
  orderConditionalBranches,
  splitBranches,
  validateBranchGroup,
  type BranchSibling,
} from "../../utils/branchOrder";
import {
  detectForbiddenUnsubscribeTokens,
  forbiddenUnsubscribeMessage,
  getSendGridTemplateDetails,
  listSendGridTemplates,
} from "../../services/templates.service";
import type { SendGridTemplate } from "../../services/templates.service";
import { useTemplateGuardEnabled } from "../../hooks/useTemplateGuardEnabled";
import {
  buildStepTemplateData,
  readStepTemplateFields,
  validateLandingPageLink,
  validatePreheader,
  validateSubjectForTemplate,
} from "../../utils/sequenceStepTemplateData";
import { FIELD_OPTIONS } from "../../types/list.types";
import type {
  SequenceStep,
  StepType,
  BranchCondition,
  DelayUnit,
  DelayMode,
  ConditionLogic,
  CreateSequenceStepInput,
  UpdateSequenceStepInput,
} from "../../types/sequence.types";
import {
  STEP_TYPE_OPTIONS,
  BRANCH_CONDITION_OPTIONS,
  DELAY_UNIT_OPTIONS,
} from "../../types/sequence.types";
import type { UUID } from "../../types/common.types";

// A single condition row for compound branch conditions
interface ConditionRow {
  type: BranchCondition;
  tag: string;
  field: string;
  value: string;
  segment_id: string;
}

const emptyCondition = (): ConditionRow => ({
  type: "default",
  tag: "",
  field: "",
  value: "",
  segment_id: "",
});

/** Placeholder id for the not-yet-created step while it sits in the sibling
 *  ordering list. Never written to the database. */
const NEW_STEP_ID = "__new_branch_step__";

interface StepEditorProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (
    input: CreateSequenceStepInput | UpdateSequenceStepInput,
  ) => Promise<void>;
  sequenceId: UUID;
  parentStepId?: UUID | null;
  editingStep?: SequenceStep | null;
  isChildStep?: boolean;
}

const StepEditor: React.FC<StepEditorProps> = ({
  isOpen,
  onClose,
  onSave,
  sequenceId,
  parentStepId = null,
  editingStep = null,
  isChildStep = false,
}) => {
  const [stepType, setStepType] = useState<StepType>("send_email");
  const [name, setName] = useState("");
  const [delayValue, setDelayValue] = useState(0);
  const [delayUnit, setDelayUnit] = useState<DelayUnit>("days");
  // Pardot parity Tier 1 (2026-05-17) — delay mode + supporting state.
  const [delayMode, setDelayMode] = useState<DelayMode>("relative");
  const [delayAbsoluteAt, setDelayAbsoluteAt] = useState<string>("");
  const [relativeField, setRelativeField] = useState<string>("");
  const [relativeOffsetValue, setRelativeOffsetValue] = useState<number>(0);
  const [relativeOffsetUnit, setRelativeOffsetUnit] =
    useState<DelayUnit>("days");
  const [evalWaitValue, setEvalWaitValue] = useState(3);
  const [evalWaitUnit, setEvalWaitUnit] = useState<DelayUnit>("days");

  // Branch conditions (supports up to 5 compound conditions)
  const [conditionLogic, setConditionLogic] = useState<ConditionLogic>("and");
  const [conditions, setConditions] = useState<ConditionRow[]>([
    emptyCondition(),
  ]);

  // Action config fields
  const [emailSubject, setEmailSubject] = useState("");
  const [emailTemplateId, setEmailTemplateId] = useState("");
  // From / Reply-To (mig 1550). From is the U.S. LawShield default or {{assigned_agent}}; Reply-To is blank, an
  // address, or {{assigned_agent}}. Unused -> the step saves exactly as before (no keys).
  const [stepFromAgent, setStepFromAgent] = useState(false);
  const [stepReplyTo, setStepReplyTo] = useState("");
  const [stepReplyToFallback, setStepReplyToFallback] = useState("");
  const stepReplyToIsAgent = isAssignedAgentToken(stepReplyTo);
  const [templateSearch, setTemplateSearch] = useState("");
  // Template merge fields for the step (2026-10-30): {{landing_page_link}}, {{preheader}}, {{subject}}.
  const [landingPageLink, setLandingPageLink] = useState("");
  const [preheader, setPreheader] = useState("");
  const [passSubject, setPassSubject] = useState(false);
  // The step's stored email_template_data, so keys this editor does not manage survive a save.
  const [storedTemplateData, setStoredTemplateData] =
    useState<unknown>(undefined);
  const [smsMessage, setSmsMessage] = useState("");
  const [tagValue, setTagValue] = useState("");
  const [listId, setListId] = useState("");
  const [attrField, setAttrField] = useState("");
  const [attrValue, setAttrValue] = useState("");
  const [taskType, setTaskType] = useState("");
  const [taskNotes, setTaskNotes] = useState("");
  const [taskAgentName, setTaskAgentName] = useState("");
  const [taskDueDays, setTaskDueDays] = useState(1);

  const [includeVoucher, setIncludeVoucher] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Branch evaluation order. null = "operator has not touched the order", in
  // which case saving leaves every sibling's branch_order exactly as stored.
  // A non-null value is an explicit operator decision and IS written on save.
  const [manualOrder, setManualOrder] = useState<string[] | null>(null);

  const queryClient = useQueryClient();

  // All lists — used for in_segment/not_in_segment branch conditions which
  // now (post-PR2) work for both static and dynamic segments via the new
  // contact_matches_segment RPC.
  //
  // IMPORTANT: wrap getLists in an arrow so React Query doesn't pass its
  // QueryFunctionContext as the optional organizationId parameter. The
  // bare `queryFn: getLists` form would invoke `getLists(ctx)` and the
  // truthy ctx triggers `.eq("organization_id", ctx)` against the lists
  // table — returning zero rows and surfacing as an empty dropdown.
  const { data: allLists } = useQuery({
    queryKey: ["lists-for-step-editor"],
    queryFn: () => getLists(),
    enabled: isOpen,
  });
  // Static-only — used for add_to_list/remove_from_list action steps.
  // Dynamic segments don't support manual membership changes; their
  // membership is determined by query_criteria evaluation.
  const staticLists = (allLists ?? []).filter((l) => !l.is_dynamic);

  const { data: sgTemplates, isLoading: templatesLoading } = useQuery({
    queryKey: ["sendgrid-templates"],
    queryFn: () => listSendGridTemplates(),
    enabled: isOpen && stepType === "send_email",
    staleTime: 5 * 60 * 1000,
  });

  // Dead-unsubscribe-link guard (audit R3-B2): fetch the selected template's merge fields and
  // block a sequence step whose template uses {{unsubscribe}}/{{unsubscribe_preferences}} —
  // those render an empty link on a CRM send. Server-side enqueue is the authoritative gate;
  // this warns the operator early. Same detection as the FlightForm gate.
  const { data: selectedTemplateDetails } = useQuery({
    queryKey: ["sendgrid-template-details", emailTemplateId],
    queryFn: () => getSendGridTemplateDetails(emailTemplateId),
    enabled: isOpen && stepType === "send_email" && !!emailTemplateId,
    staleTime: 5 * 60 * 1000,
  });
  const templateUsesLandingLink =
    selectedTemplateDetails?.merge_fields?.includes("landing_page_link") ??
    false;
  const templateUsesPreheader =
    selectedTemplateDetails?.merge_fields?.includes("preheader") ?? false;
  const landingLinkError = validateLandingPageLink(landingPageLink);
  const preheaderError = validatePreheader(preheader);
  const templateSubjectError = validateSubjectForTemplate(
    emailSubject,
    passSubject,
  );
  const forbiddenUnsubTokens = selectedTemplateDetails?.merge_fields
    ? detectForbiddenUnsubscribeTokens(selectedTemplateDetails.merge_fields)
    : [];
  // ASM template-guard flag (FG-B1): gates the dead-unsubscribe-link block below. OFF (default)
  // during deploy-dark → non-blocking migration notice; ON → hard block on save.
  const templateGuardEnabled = useTemplateGuardEnabled();

  const filteredTemplates = sgTemplates?.filter((t) => {
    if (!templateSearch.trim()) {
      return true;
    }
    const q = templateSearch.toLowerCase();
    return (
      t.name.toLowerCase().includes(q) ||
      t.active_version?.subject?.toLowerCase().includes(q)
    );
  });

  const isEditing = !!editingStep;

  // =====================================================================
  // BRANCH EVALUATION ORDER
  //
  // message_sequence_steps.branch_order decides which branch a contact takes.
  // process-sequence-steps loads the active children of the parent step
  // ORDER BY branch_order ASC, walks them, and sends the contact down the
  // FIRST one that matches. A 'default' child is pulled out of that loop and
  // used only as the fallback, so its branch_order is irrelevant.
  //
  // Until this control existed there was no way to see or set that order, and
  // two branches sitting on the same number produced a genuinely
  // nondeterministic outcome in production.
  // =====================================================================

  // On edit, the parent comes from the step itself — the parentStepId prop is
  // only set for the "add branch" path and is stale during an edit.
  const branchParentId = editingStep
    ? editingStep.parent_step_id
    : parentStepId;
  const isBranchChild = isChildStep && !!branchParentId;

  const { data: allSequenceSteps } = useQuery({
    queryKey: ["sequence-steps-flat", sequenceId],
    queryFn: () => getSequenceStepsFlat(sequenceId),
    enabled: isOpen && isBranchChild,
  });

  // The condition currently selected in this modal — the sibling list has to
  // reflect unsaved edits, otherwise switching this step to/from Default
  // would show a stale picture.
  const liveBranchCondition: string = isChildStep
    ? conditions[0]?.type || "default"
    : "default";
  const thisStepIsFallback = liveBranchCondition === "default";

  /** Every branch under the same parent, including this one (as a pending row
   *  when it does not exist yet), with this modal's unsaved edits applied. */
  const siblingGroup: BranchSibling[] = useMemo(() => {
    if (!isBranchChild) {
      return [];
    }
    const rows: BranchSibling[] = (allSequenceSteps ?? [])
      .filter((st) => st.parent_step_id === branchParentId)
      .map((st) => ({
        id: st.id,
        name: st.id === editingStep?.id ? name.trim() || st.name : st.name,
        step_type: st.step_type,
        branch_condition:
          st.id === editingStep?.id ? liveBranchCondition : st.branch_condition,
        branch_order: st.branch_order,
        created_at: st.created_at,
        is_active: st.is_active !== false,
      }));
    if (!editingStep) {
      rows.push({
        id: NEW_STEP_ID,
        name: name.trim() || null,
        step_type: stepType,
        branch_condition: liveBranchCondition,
        // Unset sorts last, so a new branch lands at the end of the queue
        // unless the operator moves it.
        branch_order: null,
        created_at: null,
        is_active: true,
      });
    }
    return rows;
  }, [
    isBranchChild,
    allSequenceSteps,
    branchParentId,
    editingStep,
    liveBranchCondition,
    name,
    stepType,
  ]);

  const naturalOrderIds = useMemo(
    () => orderConditionalBranches(siblingGroup).map((st) => st.id),
    [siblingGroup],
  );

  // A manual order only applies while it still covers exactly the same set of
  // branches. Flipping this step to Default (or a sibling changing underneath
  // us) invalidates it and we fall back to the stored order.
  const manualOrderApplies =
    manualOrder !== null &&
    manualOrder.length === naturalOrderIds.length &&
    naturalOrderIds.every((id) => manualOrder.includes(id));

  const orderedIds = manualOrderApplies
    ? (manualOrder as string[])
    : naturalOrderIds;
  /** True when saving will renumber this sibling group 0..n-1. */
  const willRenumber = manualOrderApplies;

  const siblingById = useMemo(
    () => new Map(siblingGroup.map((st) => [st.id, st])),
    [siblingGroup],
  );
  const fallbackSiblings = useMemo(
    () => splitBranches(siblingGroup).fallback,
    [siblingGroup],
  );

  // Warnings are computed from the branches the worker would actually see
  // (active only). A pending renumber resolves ties, so that warning is
  // suppressed once the operator has set an explicit order.
  const branchWarnings = useMemo(() => {
    const evaluated = siblingGroup.filter((st) => st.is_active !== false);
    const all = validateBranchGroup(evaluated);
    return willRenumber
      ? all.filter((w) => w.code !== "tied_branch_order")
      : all;
  }, [siblingGroup, willRenumber]);

  const thisStepId = editingStep?.id ?? NEW_STEP_ID;
  const thisStepPosition = orderedIds.indexOf(thisStepId);

  const moveThisOrSibling = (index: number, delta: -1 | 1) => {
    setManualOrder(moveInOrder(orderedIds, index, delta));
  };

  useEffect(() => {
    if (editingStep) {
      setStepType(editingStep.step_type);
      setName(editingStep.name || "");
      setDelayValue(editingStep.delay_value);
      setDelayUnit(editingStep.delay_unit);
      setDelayMode((editingStep.delay_mode as DelayMode) || "relative");
      setDelayAbsoluteAt(
        editingStep.delay_absolute_at
          ? editingStep.delay_absolute_at.replace("Z", "").slice(0, 16)
          : "",
      );
      // relative_to_field config lives in action_config.delay_relative_to_field.
      // Pull defaults safely.
      const rtField = (editingStep.action_config as Record<string, unknown>)
        ?.delay_relative_to_field as
        | { field?: string; offset_value?: number; offset_unit?: string }
        | undefined;
      setRelativeField(rtField?.field || "");
      setRelativeOffsetValue(rtField?.offset_value ?? 0);
      setRelativeOffsetUnit((rtField?.offset_unit as DelayUnit) || "days");
      setEvalWaitValue(editingStep.evaluation_wait_value ?? 3);
      setEvalWaitUnit(
        (editingStep.evaluation_wait_unit as DelayUnit) || "days",
      );
      setIncludeVoucher(editingStep.include_voucher || false);

      // Parse branch conditions (support legacy single + new compound)
      const bcc = editingStep.branch_condition_config || {};
      const additionalConditions = (bcc.additional_conditions || []) as Array<{
        type: string;
        tag?: string;
        field?: string;
        value?: string;
        segment_id?: string;
      }>;

      const primaryCondition: ConditionRow = {
        type: editingStep.branch_condition || "default",
        tag: (bcc.tag as string) || "",
        field: (bcc.field as string) || "",
        value: (bcc.value as string) || "",
        segment_id: (bcc.segment_id as string) || "",
      };

      const allConditions = [
        primaryCondition,
        ...additionalConditions.map((ac) => ({
          type: (ac.type as BranchCondition) || "default",
          tag: ac.tag || "",
          field: ac.field || "",
          value: ac.value || "",
          segment_id: ac.segment_id || "",
        })),
      ];
      setConditions(
        allConditions.length > 0 ? allConditions : [emptyCondition()],
      );
      setConditionLogic((bcc.condition_logic as ConditionLogic) || "and");

      // Parse action config
      const ac = editingStep.action_config || {};
      setEmailSubject((ac.email_subject as string) || "");
      setEmailTemplateId((ac.email_template_id as string) || "");
      {
        const sc = ac as Record<string, unknown>;
        setStepFromAgent(isAssignedAgentToken(sc.from_email));
        setStepReplyTo(typeof sc.reply_to === "string" ? sc.reply_to : "");
        setStepReplyToFallback(
          typeof sc.reply_to_fallback === "string" ? sc.reply_to_fallback : "",
        );
      }
      setTemplateSearch("");
      {
        const storedEtd = (ac as Record<string, unknown>).email_template_data;
        const tf = readStepTemplateFields(storedEtd);
        setLandingPageLink(tf.landingPageLink);
        setPreheader(tf.preheader);
        setPassSubject(tf.passSubject);
        setStoredTemplateData(storedEtd);
      }
      setSmsMessage((ac.sms_message_override as string) || "");
      setTagValue((ac.tag as string) || "");
      setListId((ac.list_id as string) || "");
      setAttrField((ac.field as string) || "");
      setAttrValue((ac.value as string) || "");
      setTaskType((ac.task_type as string) || "");
      setTaskNotes((ac.notes as string) || "");
      setTaskAgentName((ac.agent_name as string) || "");
      setTaskDueDays((ac.due_date_offset_days as number) || 1);
    } else {
      // Reset for new step
      setStepType("send_email");
      setName("");
      setDelayValue(0);
      setDelayUnit("days");
      setEvalWaitValue(3);
      setEvalWaitUnit("days");
      setConditions([emptyCondition()]);
      setConditionLogic("and");
      setEmailSubject("");
      setEmailTemplateId("");
      setStepFromAgent(false);
      setStepReplyTo("");
      setStepReplyToFallback("");
      setTemplateSearch("");
      setLandingPageLink("");
      setPreheader("");
      setPassSubject(false);
      setStoredTemplateData(undefined);
      setSmsMessage("");
      setTagValue("");
      setListId("");
      setAttrField("");
      setAttrValue("");
      setTaskType("");
      setTaskNotes("");
      setTaskAgentName("");
      setTaskDueDays(1);
      setIncludeVoucher(false);
    }
    setError("");
    // A fresh modal target never carries a pending reorder.
    setManualOrder(null);
  }, [editingStep]);

  // Condition helpers
  const updateCondition = (index: number, updates: Partial<ConditionRow>) => {
    setConditions((prev) =>
      prev.map((c, i) => (i === index ? { ...c, ...updates } : c)),
    );
  };

  const addCondition = () => {
    if (conditions.length < 5) {
      setConditions((prev) => [...prev, emptyCondition()]);
    }
  };

  const removeCondition = (index: number) => {
    if (conditions.length > 1) {
      setConditions((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const buildActionConfig = (): Record<string, unknown> => {
    // Pardot parity Tier 1 (2026-05-17) — relative_to_field config
    // lives in action_config so compute_step_delay can resolve it
    // server-side. Independent of step_type — applies to any step
    // whose delay_mode is relative_to_field.
    const delayRelativeBlock =
      delayMode === "relative_to_field" && relativeField
        ? {
            delay_relative_to_field: {
              field: relativeField,
              offset_value: relativeOffsetValue,
              offset_unit: relativeOffsetUnit,
            },
          }
        : {};

    switch (stepType) {
      case "send_email":
        return {
          ...delayRelativeBlock,
          email_subject: emailSubject,
          email_template_id: emailTemplateId || undefined,
          ...(stepFromAgent ? { from_email: ASSIGNED_AGENT_VAR } : {}),
          ...(stepReplyTo.trim()
            ? {
                reply_to: stepReplyToIsAgent
                  ? ASSIGNED_AGENT_VAR
                  : stepReplyTo.trim(),
              }
            : {}),
          ...(stepReplyToIsAgent && stepReplyToFallback.trim()
            ? { reply_to_fallback: stepReplyToFallback.trim() }
            : {}),
          // undefined (no key) when none of the fields is used: the step saves exactly as before.
          email_template_data: buildStepTemplateData(
            storedTemplateData,
            { landingPageLink, preheader, passSubject },
            emailSubject,
          ),
        };
      case "send_sms":
        return {
          sms_message_override: smsMessage,
        };
      case "apply_tag":
      case "remove_tag":
        return { tag: tagValue };
      case "add_to_list":
        return { list_id: listId };
      case "remove_from_list":
        return { list_id: listId };
      case "update_attribute":
        return { field: attrField, value: attrValue };
      case "create_task":
        return {
          task_type: taskType,
          notes: taskNotes,
          agent_name: taskAgentName || undefined,
          due_date_offset_days: taskDueDays,
        };
      case "wait":
        return {};
      default:
        return {};
    }
  };

  const buildConditionConfig = (c: ConditionRow): Record<string, unknown> => {
    if (c.type === "tag_applied" || c.type === "tag_not_applied") {
      return { tag: c.tag };
    }
    if (
      c.type === "contact_field_equals" ||
      c.type === "contact_field_not_equals"
    ) {
      return { field: c.field, value: c.value };
    }
    if (c.type === "in_segment" || c.type === "not_in_segment") {
      return { segment_id: c.segment_id };
    }
    return {};
  };

  const buildBranchConditionConfig = (): Record<string, unknown> => {
    const primary = conditions[0] || emptyCondition();
    const config = buildConditionConfig(primary);

    // Add additional conditions if more than one
    if (conditions.length > 1) {
      config.additional_conditions = conditions.slice(1).map((c) => ({
        type: c.type,
        ...buildConditionConfig(c),
      }));
      config.condition_logic = conditionLogic;
    }

    return config;
  };

  const handleSave = async () => {
    setError("");

    if (
      (stepType === "apply_tag" || stepType === "remove_tag") &&
      !tagValue.trim()
    ) {
      setError("Tag value is required");
      return;
    }
    if (stepType === "send_email" && !emailSubject.trim()) {
      setError("Email subject is required");
      return;
    }
    if (stepType === "send_email" && !emailTemplateId.trim()) {
      setError("SendGrid template is required");
      return;
    }
    if (stepType === "send_email") {
      const rc = checkReplyToValue(stepReplyTo);
      if (!rc.ok) {
        setError(`Reply-To: ${rc.error}`);
        return;
      }
      if (stepReplyToIsAgent) {
        const fc = checkFallbackReplyTo(stepReplyToFallback);
        if (!fc.ok) {
          setError(fc.error);
          return;
        }
      }
    }
    if (stepType === "send_email") {
      const templateFieldError =
        validateLandingPageLink(landingPageLink) ??
        validatePreheader(preheader) ??
        validateSubjectForTemplate(emailSubject, passSubject);
      if (templateFieldError) {
        setError(templateFieldError);
        return;
      }
    }
    // FLAG-GATED (audit FG-B1): only block save when the template guard is ON. While OFF
    // (deploy-dark default) a legacy {{unsubscribe}} step still saves; the inline notice below
    // switches to a non-blocking migration warning.
    if (
      templateGuardEnabled &&
      stepType === "send_email" &&
      forbiddenUnsubTokens.length > 0
    ) {
      setError(forbiddenUnsubscribeMessage(forbiddenUnsubTokens));
      return;
    }
    // Pardot Tier 1 (2026-05-17) — delay_mode validation
    if (delayMode === "absolute_date" && !delayAbsoluteAt) {
      setError("Absolute-date delay mode requires a fire-at timestamp");
      return;
    }
    if (delayMode === "relative_to_field" && !relativeField) {
      setError("Relative-to-field delay mode requires a contact date field");
      return;
    }
    if (
      stepType === "update_attribute" &&
      (!attrField.trim() || !attrValue.trim())
    ) {
      setError("Field and value are required for Update Attribute steps");
      return;
    }

    setSaving(true);
    try {
      const primaryCondition = conditions[0] || emptyCondition();

      // --- Branch evaluation order -----------------------------------------
      // Renumber the OTHER siblings first. Doing it before the step's own
      // write means a failure here leaves this step unsaved, so the operator
      // can simply press Save again — the whole operation is idempotent.
      // Nothing is written unless the operator actually reordered.
      let branchOrderForThisStep: number | undefined;
      if (isBranchChild && !thisStepIsFallback) {
        if (willRenumber && thisStepPosition >= 0) {
          branchOrderForThisStep = thisStepPosition;
        } else if (!isEditing) {
          // New branch, order untouched: append after the last existing one
          // so it can never tie with a sibling.
          branchOrderForThisStep = nextBranchOrder(
            siblingGroup.filter((st) => st.id !== NEW_STEP_ID),
          );
        }
      }
      if (isBranchChild && willRenumber) {
        const stored = new Map<string, number | null | undefined>(
          siblingGroup.map((st) => [st.id, st.branch_order]),
        );
        const writes = branchOrderWrites(orderedIds, stored).filter(
          (w) => w.id !== NEW_STEP_ID && w.id !== editingStep?.id,
        );
        if (writes.length > 0) {
          await reorderBranchSiblings(writes);
        }
      }

      if (isEditing) {
        const input: UpdateSequenceStepInput = {
          step_type: stepType,
          name: name.trim() || undefined,
          delay_value: delayValue,
          delay_unit: delayUnit,
          delay_mode: delayMode,
          delay_absolute_at:
            delayMode === "absolute_date" && delayAbsoluteAt
              ? new Date(delayAbsoluteAt).toISOString()
              : null,
          evaluation_wait_value: evalWaitValue,
          evaluation_wait_unit: evalWaitUnit,
          branch_condition: primaryCondition.type,
          branch_condition_config: buildBranchConditionConfig(),
          action_config: buildActionConfig(),
          include_voucher: includeVoucher,
          // Only sent when the operator explicitly reordered. Otherwise the
          // stored branch_order is preserved untouched.
          ...(branchOrderForThisStep !== undefined
            ? { branch_order: branchOrderForThisStep }
            : {}),
        };
        await onSave(input);
      } else {
        const input: CreateSequenceStepInput = {
          sequence_id: sequenceId,
          parent_step_id: parentStepId,
          step_type: stepType,
          name: name.trim() || undefined,
          delay_value: delayValue,
          delay_unit: delayUnit,
          delay_mode: delayMode,
          delay_absolute_at:
            delayMode === "absolute_date" && delayAbsoluteAt
              ? new Date(delayAbsoluteAt).toISOString()
              : null,
          evaluation_wait_value: evalWaitValue,
          evaluation_wait_unit: evalWaitUnit,
          branch_condition: isChildStep ? primaryCondition.type : "default",
          branch_condition_config: isChildStep
            ? buildBranchConditionConfig()
            : {},
          action_config: buildActionConfig(),
          include_voucher: includeVoucher,
          ...(branchOrderForThisStep !== undefined
            ? { branch_order: branchOrderForThisStep }
            : {}),
        };
        await onSave(input);
      }
      // Refresh the sibling list so a re-open shows the new evaluation order.
      queryClient.invalidateQueries({
        queryKey: ["sequence-steps-flat", sequenceId],
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save step");
    } finally {
      setSaving(false);
    }
  };

  /** Human label for one row of the branch evaluation queue. */
  const branchRowLabel = (st: BranchSibling): string => {
    const typeLabel =
      STEP_TYPE_OPTIONS.find((o) => o.value === st.step_type)?.label ||
      st.step_type ||
      "Step";
    const conditionLabel =
      BRANCH_CONDITION_OPTIONS.find((o) => o.value === st.branch_condition)
        ?.label || st.branch_condition;
    const nm = st.name?.trim();
    return nm
      ? `${nm} — ${conditionLabel}`
      : `${typeLabel} — ${conditionLabel}`;
  };

  // Render a single condition row
  const renderConditionRow = (condition: ConditionRow, index: number) => {
    const needsConfig = BRANCH_CONDITION_OPTIONS.find(
      (o) => o.value === condition.type,
    )?.requiresConfig;

    return (
      <div
        key={index}
        className={`space-y-2 ${index > 0 ? "pt-3 border-t border-gray-100" : ""}`}
      >
        <div className="flex items-center gap-2">
          {index > 0 && (
            <span
              className={`text-xs font-semibold px-2 py-0.5 rounded ${
                conditionLogic === "or"
                  ? "text-amber-600 bg-amber-50"
                  : "text-blue-600 bg-blue-50"
              }`}
            >
              {conditionLogic === "or" ? "OR" : "AND"}
            </span>
          )}
          <select
            value={condition.type}
            onChange={(e) =>
              updateCondition(index, {
                type: e.target.value as BranchCondition,
              })
            }
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {BRANCH_CONDITION_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label} - {opt.description}
              </option>
            ))}
          </select>
          {conditions.length > 1 && (
            <button
              type="button"
              onClick={() => removeCondition(index)}
              className="text-red-400 hover:text-red-600 text-sm px-1"
              title="Remove condition"
            >
              &times;
            </button>
          )}
        </div>

        {needsConfig && (
          <div className="ml-4">
            {(condition.type === "tag_applied" ||
              condition.type === "tag_not_applied") && (
              <Input
                label="Tag"
                value={condition.tag}
                onChange={(e) =>
                  updateCondition(index, { tag: e.target.value })
                }
                placeholder="e.g. Interested"
              />
            )}
            {(condition.type === "contact_field_equals" ||
              condition.type === "contact_field_not_equals") && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Field
                  </label>
                  <select
                    value={condition.field}
                    onChange={(e) =>
                      updateCondition(index, { field: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select field...</option>
                    {FIELD_OPTIONS.filter(
                      (f) =>
                        ![
                          "event_performed",
                          "event_not_performed",
                          "segment_membership",
                        ].includes(f.value),
                    ).map((f) => (
                      <option key={f.value} value={f.value}>
                        {f.label}
                      </option>
                    ))}
                  </select>
                </div>
                <Input
                  label="Value"
                  value={condition.value}
                  onChange={(e) =>
                    updateCondition(index, { value: e.target.value })
                  }
                  placeholder="e.g. customer"
                />
              </div>
            )}
            {(condition.type === "in_segment" ||
              condition.type === "not_in_segment") && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Segment / List
                </label>
                <select
                  value={condition.segment_id}
                  onChange={(e) =>
                    updateCondition(index, { segment_id: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select a segment...</option>
                  {/*
                    Branch conditions now support BOTH static and dynamic
                    segments — runtime evaluates dynamic criteria via the
                    new contact_matches_segment RPC. Action steps
                    (add_to_list / remove_from_list) still only allow
                    static; that picker uses staticLists below.
                  */}
                  {allLists?.map((list) => (
                    <option key={list.id} value={list.id}>
                      {list.name}
                      {list.is_dynamic ? " [dynamic]" : ""} (
                      {list.contact_count ?? 0} contacts)
                    </option>
                  ))}
                </select>
                {condition.segment_id &&
                  allLists &&
                  !allLists.some((l) => l.id === condition.segment_id) && (
                    <p className="mt-1 text-xs text-red-600">
                      ⚠ Referenced segment was deleted. Pick another or this
                      branch will resolve to{" "}
                      {condition.type === "in_segment" ? "false" : "true"} for
                      every contact.
                    </p>
                  )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Get selected template name for display
  const selectedTemplate = sgTemplates?.find((t) => t.id === emailTemplateId);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? "Edit Step" : "Add Step"}
      size="lg"
    >
      <div className="space-y-5 p-1">
        {/* Step Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Step Type
          </label>
          <div className="grid grid-cols-2 gap-2">
            {STEP_TYPE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setStepType(opt.value)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm text-left transition-colors ${
                  stepType === opt.value
                    ? "border-primary-500 bg-primary-50 text-primary-700"
                    : "border-gray-200 hover:border-gray-300 text-gray-700"
                }`}
              >
                <StepTypeIcon
                  stepType={opt.value}
                  className="h-4 w-4 flex-shrink-0"
                />
                <div>
                  <div className="font-medium">{opt.label}</div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Step Name */}
        <Input
          label="Step Name (optional)"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Welcome Email"
        />

        {/* Delay — Pardot parity Tier 1 (2026-05-17): delay_mode selector
            switches between relative (legacy behavior), absolute_date,
            and relative_to_field. Per-mode inputs render below. */}
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Delay Mode
            </label>
            <select
              value={delayMode}
              onChange={(e) => setDelayMode(e.target.value as DelayMode)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="relative">
                Relative — N minutes/hours/days/weeks after the previous step
              </option>
              <option value="absolute_date">
                Absolute date/time — fire at a specific moment
              </option>
              <option value="relative_to_field">
                Relative to contact field — N units before/after a contact date
                field (e.g. event start, member expiration)
              </option>
            </select>
          </div>

          {delayMode === "relative" && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Delay Before Execution
                </label>
                <input
                  type="number"
                  min="0"
                  value={delayValue}
                  onChange={(e) =>
                    setDelayValue(Math.max(0, parseInt(e.target.value) || 0))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Unit
                </label>
                <select
                  value={delayUnit}
                  onChange={(e) => setDelayUnit(e.target.value as DelayUnit)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {DELAY_UNIT_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {delayMode === "absolute_date" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fire at (date and time, local)
              </label>
              <input
                type="datetime-local"
                value={delayAbsoluteAt}
                onChange={(e) => setDelayAbsoluteAt(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                If this timestamp is in the past when the enrollment reaches
                this step, the step fires immediately.
              </p>
            </div>
          )}

          {delayMode === "relative_to_field" && (
            <div className="space-y-3 border border-gray-200 rounded-lg p-3 bg-gray-50">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contact Date Field
                </label>
                <select
                  value={relativeField}
                  onChange={(e) => setRelativeField(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">— Select a field —</option>
                  <option value="member_expiration_date">
                    member_expiration_date
                  </option>
                  <option value="member_renewal_date">
                    member_renewal_date
                  </option>
                  <option value="member_start_date">member_start_date</option>
                  <option value="member_end_date">member_end_date</option>
                  <option value="member_date">member_date</option>
                  <option value="date_of_birth">date_of_birth</option>
                  <option value="created_at">created_at</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Offset (use negative for before)
                  </label>
                  <input
                    type="number"
                    value={relativeOffsetValue}
                    onChange={(e) =>
                      setRelativeOffsetValue(parseInt(e.target.value) || 0)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Unit
                  </label>
                  <select
                    value={relativeOffsetUnit}
                    onChange={(e) =>
                      setRelativeOffsetUnit(e.target.value as DelayUnit)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {DELAY_UNIT_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <p className="text-xs text-gray-500">
                If the chosen field is NULL or unparseable for a contact when
                this step fires, the enrollment is rejected (and logged to the
                sequence triage page).
              </p>
            </div>
          )}
        </div>

        {/* Branch Conditions (only for child steps) */}
        {isChildStep && (
          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Branch Conditions
              </label>
              {conditions.length < 5 && (
                <button
                  type="button"
                  onClick={addCondition}
                  className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                >
                  + Add Condition
                </button>
              )}
            </div>
            <div className="space-y-2">
              {conditions.map((condition, index) =>
                renderConditionRow(condition, index),
              )}
            </div>
            {conditions.length > 1 && (
              <div className="mt-3 flex items-center gap-3">
                <span className="text-xs font-medium text-gray-500">
                  Match:
                </span>
                <div className="inline-flex rounded-md border border-gray-300 p-0.5 bg-gray-50">
                  <button
                    type="button"
                    onClick={() => setConditionLogic("and")}
                    className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                      conditionLogic === "and"
                        ? "bg-white text-gray-900 shadow-sm"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    ALL conditions
                  </button>
                  <button
                    type="button"
                    onClick={() => setConditionLogic("or")}
                    className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                      conditionLogic === "or"
                        ? "bg-white text-gray-900 shadow-sm"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    ANY condition
                  </button>
                </div>
                <span className="text-xs text-gray-400">
                  Up to 5 conditions.
                </span>
              </div>
            )}
          </div>
        )}

        {/* Branch Evaluation Order — only meaningful for a branch child.
            The worker checks a parent's children in branch_order and takes
            the first match, so this is the control that decides which branch
            a contact actually receives. */}
        {isBranchChild && (
          <div className="border-t pt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Branch Evaluation Order
            </label>
            <p className="text-xs text-gray-500 mb-3">
              When the step above finishes, the CRM checks these branches from
              the top and sends each contact down the{" "}
              <span className="font-medium">first one that matches</span>. A
              &ldquo;Default&rdquo; branch is the fallback: it is used only when
              nothing above it matched, so it has no place in the queue.
            </p>

            {branchWarnings.map((w) => (
              <div
                key={w.code}
                className="mb-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900"
              >
                <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0 mt-0.5 text-amber-600" />
                <div>
                  <div className="font-semibold">{w.message}</div>
                  <div className="mt-0.5">{w.consequence}</div>
                  {w.code === "tied_branch_order" && orderedIds.length > 1 && (
                    <button
                      type="button"
                      onClick={() => setManualOrder(naturalOrderIds)}
                      className="mt-1 font-medium underline hover:text-amber-700"
                    >
                      Number them 1, 2, 3&hellip; in the order shown below
                    </button>
                  )}
                </div>
              </div>
            ))}

            {orderedIds.length === 0 ? (
              <p className="text-xs text-gray-500">
                No branches with a condition here yet.
              </p>
            ) : (
              <ol className="space-y-1">
                {orderedIds.map((id, index) => {
                  const st = siblingById.get(id);
                  if (!st) {
                    return null;
                  }
                  const isThis = id === thisStepId;
                  return (
                    <li
                      key={id}
                      className={`flex items-center gap-2 rounded-md border px-2 py-1.5 text-sm ${
                        isThis
                          ? "border-primary-300 bg-primary-50"
                          : "border-gray-200 bg-white"
                      }`}
                    >
                      <span className="w-6 flex-shrink-0 text-center text-xs font-semibold text-gray-500">
                        {index + 1}
                      </span>
                      <span className="min-w-0 flex-1 truncate text-gray-800">
                        {branchRowLabel(st)}
                        {isThis && (
                          <span className="ml-1 text-xs font-medium text-primary-700">
                            (this step)
                          </span>
                        )}
                        {st.is_active === false && (
                          <span className="ml-1 text-xs text-gray-400">
                            (inactive &mdash; skipped at runtime)
                          </span>
                        )}
                      </span>
                      <div className="flex flex-shrink-0 items-center gap-0.5">
                        <button
                          type="button"
                          title="Check this branch earlier"
                          aria-label={`Move ${branchRowLabel(st)} earlier`}
                          disabled={index === 0}
                          onClick={() => moveThisOrSibling(index, -1)}
                          className="rounded p-1 text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:hover:bg-transparent"
                        >
                          <ChevronUpIcon className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          title="Check this branch later"
                          aria-label={`Move ${branchRowLabel(st)} later`}
                          disabled={index === orderedIds.length - 1}
                          onClick={() => moveThisOrSibling(index, 1)}
                          className="rounded p-1 text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:hover:bg-transparent"
                        >
                          <ChevronDownIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}

            {fallbackSiblings.length > 0 && (
              <div className="mt-1 space-y-1">
                {fallbackSiblings.map((st) => (
                  <div
                    key={st.id}
                    className={`flex items-center gap-2 rounded-md border border-dashed px-2 py-1.5 text-sm ${
                      st.id === thisStepId
                        ? "border-primary-300 bg-primary-50"
                        : "border-gray-200 bg-gray-50"
                    }`}
                  >
                    <span className="w-6 flex-shrink-0 text-center text-xs font-semibold text-gray-400">
                      &#8627;
                    </span>
                    <span className="min-w-0 flex-1 truncate text-gray-600">
                      {branchRowLabel(st)}
                      {st.id === thisStepId && (
                        <span className="ml-1 text-xs font-medium text-primary-700">
                          (this step)
                        </span>
                      )}
                    </span>
                    <span className="flex-shrink-0 text-xs text-gray-500">
                      fallback &mdash; no position
                    </span>
                  </div>
                ))}
              </div>
            )}

            {thisStepIsFallback ? (
              <p className="mt-2 text-xs text-gray-500">
                This step is the fallback, so it is not part of the numbered
                queue &mdash; it runs only when none of the branches above
                match. Its order number is ignored.
              </p>
            ) : willRenumber ? (
              <p className="mt-2 text-xs text-primary-700">
                Saving stores this order: the branches above become 1, 2, 3
                &hellip; as shown.
              </p>
            ) : !isEditing ? (
              <p className="mt-2 text-xs text-gray-500">
                This new branch is added at position {thisStepPosition + 1} of{" "}
                {orderedIds.length} (checked last). Move it up if it should be
                checked before the others.
              </p>
            ) : (
              <p className="mt-2 text-xs text-gray-400">
                This is the order stored today. Saving leaves it unchanged
                unless you move something.
              </p>
            )}
          </div>
        )}

        {/* Action Config (varies by step_type) */}
        <div className="border-t pt-4">
          <h3 className="text-sm font-medium text-gray-700 mb-3">
            Action Configuration
          </h3>

          {stepType === "send_email" && (
            <div className="space-y-3">
              <Input
                label="Email Subject"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                placeholder="e.g. Welcome to U.S. LawShield!"
                required
              />
              <div>
                <label
                  htmlFor="step_from"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Send from
                </label>
                <select
                  id="step_from"
                  value={stepFromAgent ? ASSIGNED_AGENT_VAR : ""}
                  onChange={(e) => setStepFromAgent(e.target.value !== "")}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">
                    U.S. LawShield (noreply@uslawshield.com)
                  </option>
                  <option value={ASSIGNED_AGENT_VAR}>
                    Contact's support agent ({ASSIGNED_AGENT_VAR})
                  </option>
                </select>
                {stepFromAgent && (
                  <p
                    className="mt-1 text-xs text-blue-900"
                    data-testid="step-from-agent-help"
                  >
                    {ASSIGNED_AGENT_HELP} Fallback sender: U.S. LawShield
                    (noreply@uslawshield.com).
                  </p>
                )}
              </div>
              <div>
                <label
                  htmlFor="step_reply_to"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Reply-To (optional)
                </label>
                <input
                  id="step_reply_to"
                  type="text"
                  value={stepReplyTo}
                  onChange={(e) => setStepReplyTo(e.target.value)}
                  placeholder={`Blank: replies go to the sender. Or an address, or ${ASSIGNED_AGENT_VAR}`}
                  maxLength={254}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                />
                {stepReplyToIsAgent && (
                  <div className="mt-2">
                    <label
                      htmlFor="step_reply_to_fallback"
                      className="block text-xs font-medium text-gray-700 mb-1"
                    >
                      Fallback Reply-To (optional, used when the contact's agent
                      can't be used)
                    </label>
                    <input
                      id="step_reply_to_fallback"
                      type="email"
                      value={stepReplyToFallback}
                      onChange={(e) => setStepReplyToFallback(e.target.value)}
                      placeholder="Blank: replies go to the sending address"
                      maxLength={254}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  SendGrid Template <span className="text-red-500">*</span>
                </label>
                {/* Search input for filtering templates */}
                <input
                  type="text"
                  value={
                    selectedTemplate && !templateSearch ? "" : templateSearch
                  }
                  onChange={(e) => {
                    setTemplateSearch(e.target.value);
                    if (!e.target.value) {
                      setEmailTemplateId("");
                    }
                  }}
                  placeholder={
                    selectedTemplate
                      ? selectedTemplate.name
                      : "Search templates..."
                  }
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    selectedTemplate
                      ? "border-green-300 bg-green-50"
                      : "border-gray-300"
                  }`}
                  onFocus={() => {
                    if (selectedTemplate) {
                      setTemplateSearch("");
                    }
                  }}
                />
                {selectedTemplate && !templateSearch && (
                  <div className="mt-1 flex items-center justify-between">
                    <span className="text-xs text-green-700">
                      Selected: {selectedTemplate.name}
                      {selectedTemplate.active_version?.subject &&
                        ` — "${selectedTemplate.active_version.subject}"`}
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        setEmailTemplateId("");
                        setTemplateSearch("");
                      }}
                      className="text-xs text-red-500 hover:text-red-700"
                    >
                      Clear
                    </button>
                  </div>
                )}
                {forbiddenUnsubTokens.length > 0 && (
                  <div
                    className={`mt-2 rounded-md border p-2 text-xs ${
                      templateGuardEnabled
                        ? "border-red-200 bg-red-50 text-red-700"
                        : "border-amber-200 bg-amber-50 text-amber-800"
                    }`}
                  >
                    ⚠️ This template uses{" "}
                    {forbiddenUnsubTokens
                      .map((t) => `{{${t.token}}}`)
                      .join(", ")}
                    , which renders an empty unsubscribe link on a CRM send. In
                    SendGrid, replace with{" "}
                    {forbiddenUnsubTokens.map((t) => t.replacement).join(", ")}.
                    {templateGuardEnabled
                      ? " You can't save this step until it's fixed."
                      : " It will still save for now, but should be migrated to a SendGrid subscription-group (ASM) unsubscribe tag."}
                  </div>
                )}
                {/* Template list (show when searching or no selection) */}
                {(templateSearch || !selectedTemplate) && (
                  <div className="mt-1 max-h-40 overflow-y-auto border border-gray-200 rounded-lg">
                    {templatesLoading ? (
                      <div className="px-3 py-2 text-sm text-gray-400">
                        Loading templates...
                      </div>
                    ) : filteredTemplates && filteredTemplates.length > 0 ? (
                      filteredTemplates.map((t: SendGridTemplate) => (
                        <button
                          key={t.id}
                          type="button"
                          onClick={() => {
                            setEmailTemplateId(t.id);
                            setTemplateSearch("");
                          }}
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 border-b border-gray-100 last:border-0 ${
                            emailTemplateId === t.id
                              ? "bg-blue-50 text-blue-700"
                              : "text-gray-700"
                          }`}
                        >
                          <div className="font-medium">{t.name}</div>
                          {t.active_version?.subject && (
                            <div className="text-xs text-gray-500">
                              Subject: {t.active_version.subject}
                            </div>
                          )}
                        </button>
                      ))
                    ) : (
                      <div className="px-3 py-2 text-sm text-gray-400">
                        {templateSearch
                          ? "No templates match your search"
                          : "No templates available"}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Template merge fields (2026-10-30). Stored in action_config.email_template_data. */}
              <div
                className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-3"
                data-testid="step-template-fields"
              >
                <div>
                  <h4 className="text-sm font-medium text-gray-700">
                    Template merge fields{" "}
                    <span className="font-normal text-gray-500">
                      (optional)
                    </span>
                  </h4>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Values passed to the SendGrid template every time this step
                    sends. Leave blank to send exactly as before.
                  </p>
                </div>
                <div>
                  <label
                    htmlFor="step-landing-page-link"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Landing page link (URL)
                  </label>
                  <input
                    id="step-landing-page-link"
                    type="url"
                    inputMode="url"
                    value={landingPageLink}
                    onChange={(e) => {
                      setLandingPageLink(e.target.value);
                      setError("");
                    }}
                    placeholder="https://www.uslawshield.com/..."
                    className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      landingLinkError ? "border-red-300" : "border-gray-300"
                    }`}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Fills <code>{"{{landing_page_link}}"}</code> in the
                    template, usually the main button. Must start with{" "}
                    <code>https://</code>. The same link goes to every contact;
                    include any tracking (UTM) parameters in the URL itself.
                    SendGrid click tracking still records clicks.
                  </p>
                  {landingLinkError && landingPageLink.trim() !== "" && (
                    <p className="text-xs text-red-600 mt-1">
                      {landingLinkError}
                    </p>
                  )}
                  {templateUsesLandingLink && landingPageLink.trim() === "" && (
                    <p
                      className="mt-1 rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800"
                      data-testid="landing-link-missing-warning"
                    >
                      This template uses <code>{"{{landing_page_link}}"}</code>.
                      Without a link here, that button or link has no
                      destination.
                    </p>
                  )}
                </div>
                <div>
                  <label
                    htmlFor="step-preheader"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Preheader
                  </label>
                  <input
                    id="step-preheader"
                    type="text"
                    value={preheader}
                    onChange={(e) => {
                      setPreheader(e.target.value);
                      setError("");
                    }}
                    placeholder="Short preview text shown after the subject in the inbox"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Fills <code>{"{{preheader}}"}</code>
                    {templateUsesPreheader
                      ? " (this template uses it)."
                      : ", if the template uses it."}
                  </p>
                  {preheaderError && (
                    <p className="text-xs text-red-600 mt-1">
                      {preheaderError}
                    </p>
                  )}
                </div>
                <label className="flex items-start gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={passSubject}
                    onChange={(e) => {
                      setPassSubject(e.target.checked);
                      setError("");
                    }}
                    className="mt-0.5"
                    data-testid="step-pass-subject"
                  />
                  <span>
                    Use this step&apos;s Email Subject inside the template
                    <span className="block text-xs text-gray-500">
                      Sends the subject above as <code>{"{{subject}}"}</code>.
                      Templates written as{" "}
                      <code>{"{{#if subject}}…{{else}}…{{/if}}"}</code> use it
                      instead of their built-in subject line. Unticked, the
                      template&apos;s own subject is used.
                    </span>
                  </span>
                </label>
                {templateSubjectError && (
                  <p
                    className="text-xs text-red-600"
                    data-testid="template-subject-error"
                  >
                    {templateSubjectError}
                  </p>
                )}
              </div>
            </div>
          )}

          {stepType === "send_sms" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                SMS Message
              </label>
              <textarea
                value={smsMessage}
                onChange={(e) => setSmsMessage(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Use {{first_name}}, {{last_name}} for merge variables"
              />
            </div>
          )}

          {(stepType === "apply_tag" || stepType === "remove_tag") && (
            <Input
              label="Tag"
              value={tagValue}
              onChange={(e) => setTagValue(e.target.value)}
              placeholder={
                stepType === "remove_tag"
                  ? "e.g. in_2023_onboard"
                  : "e.g. Onboarding: Completed Step 2"
              }
              required
            />
          )}

          {(stepType === "add_to_list" || stepType === "remove_from_list") && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Target List <span className="text-red-500">*</span>
              </label>
              <select
                value={listId}
                onChange={(e) => setListId(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select a list...</option>
                {staticLists.map((list) => (
                  <option key={list.id} value={list.id}>
                    {list.name} ({list.contact_count ?? 0} contacts)
                  </option>
                ))}
              </select>
              {listId && allLists && !allLists.some((l) => l.id === listId) && (
                <p className="mt-1 text-xs text-red-600">
                  ⚠ Referenced list was deleted. Pick another or this step will
                  fail at runtime.
                </p>
              )}
              {listId &&
                allLists?.some(
                  (l) => l.id === listId && l.is_dynamic === true,
                ) && (
                  <p className="mt-1 text-xs text-amber-600">
                    Dynamic segments can't have contacts manually added or
                    removed — their membership is determined by criteria. Pick a
                    static list.
                  </p>
                )}
              <p className="mt-1 text-xs text-gray-500">
                Only static lists are shown. Dynamic segments are excluded
                because their membership is computed from criteria.
              </p>
            </div>
          )}

          {stepType === "update_attribute" && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contact Field <span className="text-red-500">*</span>
                </label>
                <select
                  value={attrField}
                  onChange={(e) => setAttrField(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select field...</option>
                  {FIELD_OPTIONS.filter(
                    (f) =>
                      ![
                        "event_performed",
                        "event_not_performed",
                        "segment_membership",
                      ].includes(f.value),
                  ).map((f) => (
                    <option key={f.value} value={f.value}>
                      {f.label}
                    </option>
                  ))}
                </select>
              </div>
              <Input
                label="New Value"
                value={attrValue}
                onChange={(e) => setAttrValue(e.target.value)}
                placeholder="e.g. customer"
                required
              />
            </div>
          )}

          {stepType === "create_task" && (
            <div className="space-y-3">
              <Input
                label="Task Type"
                value={taskType}
                onChange={(e) => setTaskType(e.target.value)}
                placeholder="e.g. follow_up"
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  value={taskNotes}
                  onChange={(e) => setTaskNotes(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Task notes for the agent"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Input
                  label="Assign to Agent (optional)"
                  value={taskAgentName}
                  onChange={(e) => setTaskAgentName(e.target.value)}
                  placeholder="Agent name"
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Due in (days)
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={taskDueDays}
                    onChange={(e) =>
                      setTaskDueDays(Math.max(1, parseInt(e.target.value) || 1))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>
          )}

          {stepType === "wait" && (
            <p className="text-sm text-gray-500">
              This step simply waits for the delay configured above before
              proceeding to evaluate branch conditions.
            </p>
          )}
        </div>

        {/* Evaluation Wait (for steps that may have children) */}
        <div className="border-t pt-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Evaluation Wait (before checking branch conditions for children)
          </label>
          <div className="grid grid-cols-2 gap-3">
            <input
              type="number"
              min="0"
              value={evalWaitValue}
              onChange={(e) =>
                setEvalWaitValue(Math.max(0, parseInt(e.target.value) || 0))
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <select
              value={evalWaitUnit}
              onChange={(e) => setEvalWaitUnit(e.target.value as DelayUnit)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {DELAY_UNIT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <p className="text-xs text-gray-400 mt-1">
            After this step executes, wait this long before evaluating which
            child branch to take. Default: 3 days.
          </p>
        </div>

        {/* Voucher */}
        {(stepType === "send_email" || stepType === "send_sms") && (
          <div className="border-t pt-4">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={includeVoucher}
                onChange={(e) => setIncludeVoucher(e.target.checked)}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="font-medium text-gray-700">Include Voucher</span>
            </label>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
            {error}
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : isEditing ? "Update Step" : "Add Step"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default StepEditor;
